import './App.css';
import { useEffect, useLayoutEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';
import LandingPage from './pages/LandingPage';
import NewsPage from './pages/NewsPage';
import NewsDetailPage from './pages/NewsDetailPage';
import AdminLogin from './pages/dadu/AdminLogin';
import AdminNews from './pages/dadu/AdminNews';
import AdminContacts from './pages/dadu/AdminContacts';
import AdminCustomers from './pages/dadu/AdminCustomers';
import AdminCustomerRegistrations from './pages/dadu/AdminCustomerRegistrations';
import AdminBilling from './pages/dadu/AdminBilling';
import AdminFinance from './pages/dadu/AdminFinance';
import AdminSettings from './pages/dadu/AdminSettings';
import AdminFtthDesign from './pages/dadu/AdminFtthDesign';
import AdminConnectivityMap from './pages/dadu/AdminConnectivityMap';
import TopologyWorkspaceUnified from './pages/dadu/TopologyWorkspaceUnified';
import AdminFieldHandling from './pages/dadu/AdminFieldHandling';
import AdminAIControlCenter from './pages/dadu/AdminAIControlCenter';
import AdminIncidentClusterDetail from './pages/dadu/AdminIncidentClusterDetail';
import AdminUsers from './pages/dadu/AdminUsers';
import AdminVoucherSystem from './pages/dadu/AdminVoucherSystem';
import AdminInfrastructure from './pages/dadu/AdminInfrastructure';
import AdminHotspotProfileManagement from './pages/dadu/AdminHotspotProfileManagement';
import AdminPppProfileManagement from './pages/dadu/AdminPppProfileManagement';
import AdminExecutiveCommandCenter from './pages/dadu/AdminExecutiveCommandCenter';
import AdminAuditLog from './pages/dadu/AdminAuditLog';
import AdminRecoveryControlCenter from './pages/dadu/AdminRecoveryControlCenter';
import AdminRecoveryRolloutDashboard from './pages/dadu/AdminRecoveryRolloutDashboard';
import AdminTickets from './pages/dadu/AdminTickets';
import AdminOperationsOverview from './pages/dadu/AdminOperationsOverview';
import AdminExecutionLogs from './pages/dadu/AdminExecutionLogs';
import AdminTriageQueue from './pages/dadu/AdminTriageQueue';
import AdminNetworkIntelligence from './pages/dadu/AdminNetworkIntelligence';
import AdminCapacityPlanning from './pages/dadu/AdminCapacityPlanning';
import AdminOpenPlatform from './pages/dadu/AdminOpenPlatform';
import AdminCustomerExperience from './pages/dadu/AdminCustomerExperience';
import AdminTenantAdmin from './pages/dadu/AdminTenantAdmin';
import RouterMonitoringPage from './features/router-monitoring/pages/RouterMonitoringPage';
import ProtectedRoute from './components/ProtectedRoute';
import CustomerLogin from './pages/CustomerLogin';
import CustomerPortal from './pages/CustomerPortal';
import AdminWhatsAppLive from './pages/dadu/AdminWhatsAppLive';
import AdminWhatsAppTemplates from './pages/dadu/AdminWhatsAppTemplates';
import CustomerProtectedRoute from './components/CustomerProtectedRoute';
import AgentProtectedRoute from './components/AgentProtectedRoute';
import TechnicianPortal from './pages/TechnicianPortal';
import InvestorPortal from './pages/InvestorPortal';
import TechnicianPerformancePage from './pages/TechnicianPerformancePage';
import NocPortal from './pages/NocPortal';
// Legacy sidebar/topbar removed in favor of Command Center AppShell
import AgentLogin from './pages/AgentLogin';
import AgentPortal from './pages/AgentPortal';
import MapErrorBoundary from './components/MapErrorBoundary';
import PageErrorBoundary from './components/PageErrorBoundary';
import ThemeModeToggleFloating from './components/ThemeModeToggleFloating';
import { clearStaffSession } from './utils/runtimeConfig';
import { getPreferredThemeMode, onThemeModeChange } from './utils/themeMode';

function RouteThemeSync() {
  const location = useLocation();
  const [themeMode, setThemeMode] = useState(() => getPreferredThemeMode());

  useEffect(() => onThemeModeChange(setThemeMode), []);

  useLayoutEffect(() => {
    const pathname = location.pathname || '';
    const isStaffRoute = (
      pathname.startsWith('/dadu')
      || pathname.startsWith('/portal')
      || pathname.startsWith('/teknisi')
      || pathname.startsWith('/investor')
      || pathname.startsWith('/noc')
      || pathname.startsWith('/performa-teknisi')
      || pathname.startsWith('/agen')
    );

    const isAdminSidebarRoute = pathname.startsWith('/dadu') && pathname !== '/dadu/login';
    const useLightMode = isStaffRoute && themeMode === 'light';

    document.body.classList.toggle('ktc-future-theme', isStaffRoute);
    document.body.classList.toggle('unified-admin-sidebar', isAdminSidebarRoute);
    document.body.classList.toggle('staff-light-theme', useLightMode);
    document.body.classList.toggle('ktc-theme-light', useLightMode);
    document.body.classList.toggle('ktc-theme-dark', isStaffRoute && !useLightMode);
    return () => {
      document.body.classList.remove('ktc-future-theme');
      document.body.classList.remove('unified-admin-sidebar');
      document.body.classList.remove('staff-light-theme');
      document.body.classList.remove('ktc-theme-light');
      document.body.classList.remove('ktc-theme-dark');
    };
  }, [location.pathname, themeMode]);

  return null;
}

function AppRoutes() {
  const LegacyVoucherRedirect = () => {
    const location = useLocation();
    const params = new URLSearchParams(location.search || '');
    const tab = String(params.get('tab') || '').toLowerCase();
    const section = ['owners', 'commissions', 'owner', 'reseller'].includes(tab) ? 'reseller' : 'voucher';
    return <Navigate to={`/dadu/hotspot/${section}${location.search || ''}`} replace />;
  };
  const LegacyMonitoringRedirect = () => {
    const location = useLocation();
    const params = new URLSearchParams(location.search || '');
    const nextParams = new URLSearchParams(params);

    const sourceType = String(nextParams.get('sourceType') || '').trim();
    const sourceId = String(nextParams.get('sourceId') || '').trim();
    if (!sourceType || !sourceId) {
      const legacyMap = [
        ['odp', 'odp'],
        ['olt', 'olt'],
        ['nas', 'nas'],
        ['customer', 'customer'],
      ];
      for (const [legacyKey, mappedType] of legacyMap) {
        const legacyValue = String(nextParams.get(legacyKey) || '').trim();
        if (!legacyValue) continue;
        nextParams.set('sourceType', mappedType);
        nextParams.set('sourceId', legacyValue);
        break;
      }
    }

    const query = nextParams.toString();
    return <Navigate to={`/dadu/monitoring${query ? `?${query}` : ''}`} replace />;
  };
  const LegacyOltPageRedirect = () => {
    const location = useLocation();
    const params = new URLSearchParams(location.search || '');
    params.set('context', 'planning');
    params.set('planningAction', 'olt');
    const query = params.toString();
    return <Navigate to={`/dadu/ftth?${query}`} replace />;
  };
  const LegacyOdpPageRedirect = () => {
    const location = useLocation();
    const params = new URLSearchParams(location.search || '');
    params.set('context', 'planning');
    params.set('planningAction', 'odp');
    const query = params.toString();
    return <Navigate to={`/dadu/ftth?${query}`} replace />;
  };
  const LegacyVoucherPageRedirect = () => {
    const location = useLocation();
    const params = new URLSearchParams(location.search || '');
    const tab = String(params.get('tab') || '').toLowerCase();
    const section = ['owners', 'commissions', 'owner', 'reseller'].includes(tab) ? 'reseller' : 'voucher';
    const query = params.toString();
    return <Navigate to={`/dadu/hotspot/${section}${query ? `?${query}` : ''}`} replace />;
  };
  return (
    <>
      <RouteThemeSync />
      {/* Command Center shell is mounted per-page via AppShell */}
      <ThemeModeToggleFloating />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/berita" element={<NewsPage />} />
        <Route path="/berita/:id" element={<NewsDetailPage />} />

        <Route path="/portal/login" element={<CustomerLogin />} />
        <Route
          path="/portal"
          element={(
            <CustomerProtectedRoute>
              <CustomerPortal />
            </CustomerProtectedRoute>
          )}
        />

        <Route path="/dadu/login" element={<AdminLogin />} />
        <Route
          path="/command-center"
          element={(
            <ProtectedRoute allowedRoles={['admin', 'noc', 'manager', 'owner', 'investor', 'superadmin']}>
              <AdminExecutiveCommandCenter />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/topology"
          element={(
            <ProtectedRoute allowedRoles={['admin', 'noc', 'manager', 'owner', 'investor', 'superadmin']}>
              <MapErrorBoundary resetKey="topology-workspace-unified">
                <TopologyWorkspaceUnified />
              </MapErrorBoundary>
            </ProtectedRoute>
          )}
        />
        <Route
          path="/recovery"
          element={(
            <ProtectedRoute allowedRoles={['admin', 'noc', 'manager', 'owner', 'superadmin']}>
              <AdminRecoveryControlCenter />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/customers"
          element={(
            <ProtectedRoute allowedRoles={['admin', 'noc', 'manager', 'owner', 'investor', 'superadmin']}>
              <AdminCustomers />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/infrastructure"
          element={(
            <ProtectedRoute allowedRoles={['admin', 'noc', 'manager', 'owner', 'investor', 'superadmin']}>
              <AdminInfrastructure />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/triage"
          element={(
            <ProtectedRoute allowedRoles={['admin', 'noc', 'manager', 'owner', 'investor', 'superadmin']}>
              <AdminTriageQueue />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/command-center/triage"
          element={(
            <ProtectedRoute allowedRoles={['admin', 'noc', 'manager', 'owner', 'investor', 'superadmin']}>
              <Navigate to="/triage" replace />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/operations"
          element={(
            <ProtectedRoute allowedRoles={['admin', 'noc', 'manager', 'owner', 'investor', 'superadmin']}>
              <AdminOperationsOverview />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/tickets"
          element={(
            <ProtectedRoute allowedRoles={['admin', 'noc', 'manager', 'owner', 'investor', 'superadmin']}>
              <AdminTickets />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/execution-logs"
          element={(
            <ProtectedRoute allowedRoles={['admin', 'noc', 'manager', 'owner', 'investor', 'superadmin']}>
              <AdminExecutionLogs />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/operations/logs"
          element={(
            <ProtectedRoute allowedRoles={['admin', 'noc', 'manager', 'owner', 'investor', 'superadmin']}>
              <Navigate to="/execution-logs" replace />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/ai-console"
          element={(
            <ProtectedRoute allowedRoles={['admin', 'noc', 'manager', 'owner', 'investor', 'superadmin']}>
              <AdminAIControlCenter />
            </ProtectedRoute>
          )}
        />

        <Route
          path="/dadu"
          element={(
            <ProtectedRoute allowedRoles={['admin']}>
              <Navigate to="/command-center" replace />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/pelanggan"
          element={(
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminCustomers />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/pengajuan-pelanggan"
          element={(
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminCustomerRegistrations />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/ftth"
          element={(
            <ProtectedRoute allowedRoles={['admin']}>
              <MapErrorBoundary resetKey="admin-ftth-design">
                <AdminFtthDesign />
              </MapErrorBoundary>
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/ftth/cluster"
          element={(
            <ProtectedRoute allowedRoles={['admin']}>
              <Navigate to="/dadu/ftth?view=cluster" replace />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/olt"
          element={(
            <ProtectedRoute allowedRoles={['admin']}>
              <LegacyOltPageRedirect />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/odp"
          element={(
            <ProtectedRoute allowedRoles={['admin']}>
              <LegacyOdpPageRedirect />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/monitoring"
          element={(
            <ProtectedRoute allowedRoles={['admin']}>
              <MapErrorBoundary resetKey="admin-connectivity-map">
                <AdminConnectivityMap />
              </MapErrorBoundary>
            </ProtectedRoute>
          )}
        />
        <Route
          path="/monitoring"
          element={(
            <ProtectedRoute allowedRoles={['admin']}>
              <LegacyMonitoringRedirect />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/penanganan"
          element={(
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminFieldHandling />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/ai-control"
          element={(
            <ProtectedRoute allowedRoles={['admin', 'noc']}>
              <AdminAIControlCenter />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/executive-command-center"
          element={(
            <ProtectedRoute allowedRoles={['owner', 'manager', 'investor', 'admin', 'noc']}>
              <AdminExecutiveCommandCenter />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/audit-logs"
          element={(
            <ProtectedRoute allowedRoles={['owner', 'manager', 'investor', 'admin', 'noc']}>
              <AdminAuditLog />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/recovery-control-center"
          element={(
            <ProtectedRoute allowedRoles={['admin', 'noc', 'manager']}>
              <AdminRecoveryControlCenter />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/recovery-rollout"
          element={(
            <ProtectedRoute allowedRoles={['admin', 'noc', 'manager', 'owner']}>
              <AdminRecoveryRolloutDashboard />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/tickets"
          element={(
            <ProtectedRoute allowedRoles={['admin', 'noc', 'manager', 'owner', 'investor', 'superadmin']}>
              <Navigate to="/tickets" replace />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/network-intelligence"
          element={(
            <ProtectedRoute allowedRoles={['admin', 'noc', 'manager', 'owner', 'investor']}>
              <PageErrorBoundary resetKey="admin-network-intelligence">
                <AdminNetworkIntelligence />
              </PageErrorBoundary>
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/capacity-planning"
          element={(
            <ProtectedRoute allowedRoles={['admin', 'noc', 'manager', 'owner', 'investor']}>
              <PageErrorBoundary resetKey="admin-capacity-planning">
                <AdminCapacityPlanning />
              </PageErrorBoundary>
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/open-platform"
          element={(
            <ProtectedRoute allowedRoles={['admin', 'owner']}>
              <AdminOpenPlatform />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/customer-experience"
          element={(
            <ProtectedRoute allowedRoles={['admin', 'noc', 'manager', 'owner']}>
              <PageErrorBoundary resetKey="admin-customer-experience">
                <AdminCustomerExperience />
              </PageErrorBoundary>
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/tenant-admin"
          element={(
            <ProtectedRoute allowedRoles={['admin', 'owner', 'manager']}>
              <PageErrorBoundary resetKey="admin-tenant-admin">
                <AdminTenantAdmin />
              </PageErrorBoundary>
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/ai-clusters/:clusterId"
          element={(
            <ProtectedRoute allowedRoles={['admin', 'noc']}>
              <AdminIncidentClusterDetail />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/whatsapp-live"
          element={(
            <ProtectedRoute allowedRoles={['admin']}>
              <PageErrorBoundary resetKey="admin-whatsapp-live">
                <AdminWhatsAppLive />
              </PageErrorBoundary>
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/whatsapp-templates"
          element={(
            <ProtectedRoute allowedRoles={['admin', 'noc']}>
              <AdminWhatsAppTemplates />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/billing"
          element={(
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminBilling />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/keuangan"
          element={(
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminFinance />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/berita"
          element={(
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminNews />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/kontak"
          element={(
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminContacts />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/pengaturan"
          element={(
            <ProtectedRoute allowedRoles={['admin']}>
              <PageErrorBoundary resetKey="admin-settings">
                <AdminSettings />
              </PageErrorBoundary>
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/voucher"
          element={(
            <ProtectedRoute allowedRoles={['admin']}>
              <LegacyVoucherRedirect />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/hotspot"
          element={(
            <ProtectedRoute allowedRoles={['admin']}>
              <Navigate to="/dadu/hotspot/overview" replace />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/hotspot/:section"
          element={(
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminVoucherSystem />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/vouchers"
          element={(
            <ProtectedRoute allowedRoles={['admin']}>
              <LegacyVoucherPageRedirect />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/infrastruktur"
          element={(
            <ProtectedRoute allowedRoles={['admin']}>
              <PageErrorBoundary resetKey="admin-infrastructure">
                <AdminInfrastructure />
              </PageErrorBoundary>
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/router-monitoring"
          element={(
            <ProtectedRoute allowedRoles={['admin', 'superadmin', 'noc', 'technician', 'investor']}>
              <RouterMonitoringPage />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/network-ops-dashboard"
          element={(
            <ProtectedRoute allowedRoles={['admin', 'superadmin', 'noc', 'technician', 'investor', 'manager', 'owner']}>
              <Navigate to="/dadu/monitoring" replace />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/manajemen/bandwidth"
          element={<Navigate to="/dadu/manajemen/ppp-profile" replace />}
        />
        <Route
          path="/dadu/manajemen/group-profile"
          element={<Navigate to="/dadu/manajemen/ppp-profile" replace />}
        />
        <Route
          path="/dadu/manajemen/hotspot-profile"
          element={(
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminHotspotProfileManagement />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/manajemen/ppp-profile"
          element={(
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminPppProfileManagement />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/dadu/users"
          element={(
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminUsers />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/performa-teknisi"
          element={(
            <ProtectedRoute allowedRoles={['admin', 'technician', 'investor', 'noc']}>
              <TechnicianPerformancePage />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/teknisi"
          element={(
            <ProtectedRoute allowedRoles={['technician']}>
              <MapErrorBoundary resetKey="technician-portal">
                <TechnicianPortal />
              </MapErrorBoundary>
            </ProtectedRoute>
          )}
        />
        <Route
          path="/investor"
          element={(
            <ProtectedRoute allowedRoles={['investor']}>
              <MapErrorBoundary resetKey="investor-portal">
                <InvestorPortal />
              </MapErrorBoundary>
            </ProtectedRoute>
          )}
        />
        <Route
          path="/investor/present"
          element={(
            <ProtectedRoute allowedRoles={['investor']}>
              <Navigate to="/investor?present=1" replace />
            </ProtectedRoute>
          )}
        />
        <Route
          path="/noc"
          element={(
            <ProtectedRoute allowedRoles={['admin', 'noc']}>
              <MapErrorBoundary resetKey="noc-portal">
                <NocPortal />
              </MapErrorBoundary>
            </ProtectedRoute>
          )}
        />
        <Route path="/agen/login" element={<AgentLogin />} />
        <Route
          path="/agen"
          element={(
            <AgentProtectedRoute>
              <AgentPortal />
            </AgentProtectedRoute>
          )}
        />
      </Routes>
    </>
  );
}

function App() {
  const [queryClient] = useState(() => new QueryClient());

  const clearCustomerSessionFallback = () => {
    localStorage.removeItem('ktc_customer_token');
    localStorage.removeItem('ktc_customer_user');
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_user');
  };

  useEffect(() => {
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const statusCode = error?.response?.status;
        if (statusCode === 401 && typeof window !== 'undefined') {
          const currentPath = String(window.location?.pathname || '');
          const onCustomerPortal = currentPath.startsWith('/portal');

          if (onCustomerPortal) {
            clearCustomerSessionFallback();
            if (currentPath !== '/portal/login') {
              window.location.href = '/portal/login';
            }
          } else {
            clearStaffSession();
            if (currentPath !== '/dadu/login') {
              window.location.href = '/dadu/login';
            }
          }
        }
        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  return (
    <div className="App">
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </QueryClientProvider>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;




